<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { useRootStore } from "@core/stores/root.store"
import type { Services } from "@services"
import type { CompanyMultipleCompanyPreviewItem } from "@core/services/company.service"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Choisissez l'entreprise pour se connecter",
            workSpaces: "Mes espaces de travail",
            perso: "Mon espace",
            role: ({ named }: { named: (key: string) => string }) => {
                const roles = {
                    employe: "Membre",
                    administrateur: "Admin",
                    owner: "Super Admin",
                    employe_read_only: "Utilisateur",
                }
                return roles?.[named("role") as keyof typeof roles] || ""
            },
            backToLogin: "Retour à la connexion",
            login: "Se connecter",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
defineEmits<{ (e: "click:login"): void }>()

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const configStore = useConfigStore()
const rootStore = useRootStore()
const { $getCompanyMultipleCompanyPreview, $updateCurrentUser } =
    inject<Services>("services")!

// ================================
// STATE
// ================================
/** Liste des entreprises de l'utilisateur (prévisualisation multi-entreprise) */
const companies = ref<CompanyMultipleCompanyPreviewItem[]>([])
/** Id de l'entreprise sélectionnée */
const selectedCompanyId = ref("")

/** Wrapper chargement liste entreprises */
const {
    fetch: companiesFetch,
    isFetching: isCompaniesFetching,
    hasError: hasCompaniesFetchError,
    error: companiesFetchError,
} = useFetchWrapper(
    async () => {
        const userId = (route.query.user as string) || ""
        if (!userId) return
        const result = await $getCompanyMultipleCompanyPreview(userId)
        companies.value = result ?? []
    },
    (error) => {
        // Sécurité : un 401 signifie que l'aperçu a été demandé sans
        // authentification valide (token absent/expiré). On renvoie vers la
        // connexion plutôt que d'afficher une erreur brute dans le sélecteur.
        const e = error as Error & { status?: number | null; code?: string }
        if (e.status === 401 || e.code === "required_auth") {
            router.push("/login")
        }
    }
)

/** Wrapper connexion à l'entreprise sélectionnée (update last_login, fetchUser, scope, redirection) */
const {
    fetch: connectFetch,
    isFetching: isConnectFetching,
    hasError: hasConnectFetchError,
    error: connectFetchError,
} = useFetchWrapper(
    async (companyId?: unknown) => {
        const id = companyId as string
        if (!id) return
        await $updateCurrentUser({
            last_login: { _id_company: id },
        })
        await authStore.fetchUser()
        rootStore.resetAllStores(["auth"])
        await configStore.fetchScope()
        await router.push("/")
    },
    (error) => {
        console.error("Erreur lors de la connexion à l'entreprise:", error)
        router.push("/")
    }
)

// ================================
// COMPUTED
// ================================

/**
 * Indique si les abonnements sont tous annulés.
 */
function isSubscriptionCanceled(
    subscriptions?: Array<{ status?: string }>
): boolean {
    if (!subscriptions?.length) return false
    return subscriptions.every((s) => s.status === "canceled")
}

/**
 * Espaces de travail = entreprises avec abonnement (plan !== 'autre').
 * Si vide, on n'affiche ni le titre ni la liste.
 */
const getWorkSpaces = computed(() =>
    companies.value
        .filter((c) => {
            const subs = c.operation?.stripe?.subscriptions
            const plan = c.operation?.stripe?.plan
            return !!subs?.length && plan !== "autre"
        })
        .map((c) => ({
            ...c,
            disabled: isSubscriptionCanceled(
                c.operation?.stripe?.subscriptions
            ),
        }))
)

/**
 * Espaces perso = entreprises sans abonnement ou plan 'autre', et rôle !== 'client'.
 */
const getCompanyPerso = computed(() =>
    companies.value
        .filter((c) => {
            const subs = c.operation?.stripe?.subscriptions
            const plan = c.operation?.stripe?.plan
            return (!subs?.length || plan === "autre") && c.role !== "client"
        })
        .map((c) => ({
            ...c,
            disabled: isSubscriptionCanceled(
                c.operation?.stripe?.subscriptions
            ),
        }))
)

// ================================
// LIFECYCLE
// ================================
onMounted(() => companiesFetch())

// ================================
// INTERACTIONS
// ================================
/**
 * Sélection d'une entreprise (ignorée si entreprise désactivée).
 * @param company - Entreprise sélectionnée
 */
function onClickCompany(
    company: CompanyMultipleCompanyPreviewItem & { disabled?: boolean }
): void {
    if (company.disabled) return
    selectedCompanyId.value = company._id
}

/**
 * Déclenche la connexion à l'entreprise sélectionnée (délègue à connectFetch).
 */
function onConnectClick(): void {
    if (!selectedCompanyId.value) return
    connectFetch(selectedCompanyId.value)
}
</script>

<template>
    <BaseCard>
        <template #title>
            <FormAuthHeader :title="t('title')" />
        </template>
        <template #content>
            <div class="flex flex-col gap-4 w-full py-6">
                <BaseMessage v-if="hasCompaniesFetchError" severity="error">
                    {{ companiesFetchError?.message }}
                </BaseMessage>
                <BaseMessage v-if="hasConnectFetchError" severity="error">
                    {{ connectFetchError?.message }}
                </BaseMessage>
                <div v-if="isCompaniesFetching" class="flex justify-center p-4">
                    <BaseProgressSpinner style="width: 2rem; height: 2rem" />
                </div>
                <template v-else>
                    <!-- Espaces de travail (entreprises avec abonnement) : titre + liste uniquement s'il y en a -->
                    <div
                        v-if="getWorkSpaces.length"
                        class="flex flex-col gap-6"
                    >
                        <h4
                            class="text-subtitle4 font-semibold text-surface-700"
                        >
                            {{ t("workSpaces") }}
                        </h4>
                        <div class="flex flex-col gap-2">
                            <div
                                v-for="(company, index) in getWorkSpaces"
                                :key="`work-${index}`"
                                class="flex justify-between gap-2 p-4 border border-surface-200 rounded cursor-pointer hover:bg-surface-50"
                                :class="{
                                    'border-primary-700!':
                                        selectedCompanyId === company._id,
                                    'opacity-60 cursor-not-allowed':
                                        company.disabled,
                                }"
                                @click="onClickCompany(company)"
                            >
                                <ItemCompany :company="company" />
                                <div class="flex items-center gap-2">
                                    <div
                                        v-if="company.img?.crop_image"
                                        class="rounded-full border border-surface-200 p-2"
                                    >
                                        <img
                                            :src="company.img?.crop_image"
                                            :alt="company.name"
                                            class="w-8 h-8 rounded-full"
                                        />
                                    </div>
                                    <p
                                        v-if="company.role"
                                        class="text-sm text-surface-600"
                                    >
                                        {{ t("role", { role: company.role }) }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Espaces perso -->
                    <div
                        v-if="getCompanyPerso.length"
                        class="flex flex-col gap-6 mt-6"
                    >
                        <h4
                            class="text-subtitle4 font-semibold text-surface-700"
                        >
                            {{ t("perso") }}
                        </h4>
                        <div class="flex flex-col gap-2">
                            <div
                                v-for="(company, index) in getCompanyPerso"
                                :key="`perso-${index}`"
                                class="flex justify-between gap-2 p-4 border border-surface-200 rounded cursor-pointer hover:bg-surface-50"
                                :class="{
                                    'border-primary-700!':
                                        selectedCompanyId === company._id,
                                    'opacity-60 cursor-not-allowed':
                                        company.disabled,
                                }"
                                @click="onClickCompany(company)"
                            >
                                <ItemCompany :company="company" />
                                <div class="flex items-center gap-2">
                                    <div
                                        v-if="company.img?.crop_image"
                                        class="rounded-full border border-surface-200 p-2"
                                    >
                                        <img
                                            :src="company.img?.crop_image"
                                            :alt="company.name"
                                            class="w-8 h-8 rounded-full"
                                        />
                                    </div>
                                    <p
                                        v-if="company.role"
                                        class="text-sm text-surface-600"
                                    >
                                        {{ t("role", { role: company.role }) }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <BaseButton
                    :loading="isConnectFetching"
                    :disabled="!selectedCompanyId || isConnectFetching"
                    :label="t('login')"
                    class="mt-4 w-full"
                    @click="onConnectClick"
                />
            </div>
        </template>
    </BaseCard>
</template>
